import { Collection, DatePart, Logger, MixinInterface, ModelRow, UserSettings } from "@mcleod/core";
import { ClickEvent, Component, DataSourceRemoteDataChangeEvent, DataSourceSubscriberErrorEvent, Label, Layout, ModelDataChangeType, Snackbar, SnackbarOptions, Table, TableRow, Toast } from "@mcleod/components";
import { LMEUserSettings } from "@mcleod/general";
import { ModelOrderPlanningProfile, RowOrderPlanningProfile } from "../models/ModelOrderPlanningProfile";
import { OrderPlanningFilter } from "./PlanningProfileFilter";
import { OrderPlanningToolPanel } from "./OrderPlanningToolPanel";
import { CallBackScheduler } from "@mcleod/common/src/CallBackScheduler";
import { LabelStyles } from "@mcleod/components/src/components/label/LabelStyles";
import { PlanningUtil } from "./PlanningUtil";

export type FindNearProps = {
    fn_move_id: string;
    fn_orig_latitude: number;
    fn_orig_longitude: number;
    fn_orig_city: string;
    fn_orig_state: string;
    fn_orig_zip: string;
}

export class OrderPlanningLayout implements MixinInterface {
    private _profile: RowOrderPlanningProfile;
    private filterObject: OrderPlanningFilter;
    protected timer: CallBackScheduler;
    private showingErrorToast: boolean;
    private _findNearProps: FindNearProps;

    get name(): string {
        return "OrderPlanningLayout";
    }

    initialize() {
    }

    get listenerDefs(): Collection<any> {
        return {};
    }

    // Provide a way to get the object this is mixed into as a real Layout (since this mixin class is a plain Object)
    getThisAsLayout(): Layout {
        return null;
    }

    // Provide a way to get the table that the layout renders
    getTable(): Table {
        return null;
    }

    // Provide a way to get the planning tools panel from the table that the layout renders
    getToolsPanel(): OrderPlanningToolPanel {
        return null;
    }

    // Provide a way to get a logger from the layout class
    getLog(): Logger {
        return null;
    }

    applyCommonTableSetup(table: Table) {
        table.overrideUrlParams = (selectedRow: TableRow) => {
            return {key: selectedRow.data.get("movement_id"), orderId: selectedRow.data.get("order_id", "none")}
        };
        table.allowDetailForRow = (row: TableRow): boolean => {
            const data = row.data as ModelRow;
            const currentCompany = UserSettings.getSingleton().getCompanyId();
            const moveCompany = data.get("company_id");
            if (moveCompany !== currentCompany) {
                const message = `This is a ${moveCompany} move. Only ${currentCompany} moves can be opened.`;
                Snackbar.showWarningSnackbar(message);
                return false;
            }
            return true;
        }
        table.headingPanel.wrap = false;
    }

    async loadProfile(profile?: string | ModelRow) {
        if (profile instanceof ModelRow) {
            await this.setProfile(profile);
            return;
        }
        await this.loadProfileFromString(profile ?? LMEUserSettings.get()?.order_planning_profile);
    }

    private async loadProfileFromString(profileId: string) {
        if (profileId == null) {
            const options: Partial<SnackbarOptions> = { persist: true, id: "noOrderPlanningProfileDefined" };
            Snackbar.showSnackbar("You do not have a default operations management profile defined. "
                + "Add a default profile to your preferences so your orders will automatically "
                + "display or you can select a profile above to get started.", options);
        }
        else {
            const profile = await new ModelOrderPlanningProfile().searchSingle({id: profileId});
            if (profile == null) {
                const options: Partial<SnackbarOptions> = { persist: true, id: "orderPlanningProfileNotFound" };
                Snackbar.showSnackbar("The profile [" + profileId + "] was not found.", options);
            }
            else
                this.setProfile(profile);
        }
    }

    public get profile(): RowOrderPlanningProfile {
        return this._profile;
    }

    private async setProfile(value: RowOrderPlanningProfile) {
        if (value != null && value != this._profile) {
            this._profile = value;
            this.filterObject = new OrderPlanningFilter(value);
            await this.getThisAsLayout().search(null);
            this.doAfterProfileSet(this._profile);
        }
    }

    doAfterProfileSet(_profile: RowOrderPlanningProfile) {
    }

    displayArriveLateTime(tableRow: TableRow, lateTimeComponentId: string, lateField: string,
        sameDayWindow: boolean, lateDatePanelId: string) {
        const timeLabel = tableRow.findComponentById(lateTimeComponentId) as Label;
        timeLabel.text = " - " + timeLabel.text;
        timeLabel.visible = sameDayWindow;
        tableRow.findComponentById(lateDatePanelId).visible = !sameDayWindow && tableRow.data.get(lateField) != null;
    }

    testUpdateFreshness(event: DataSourceRemoteDataChangeEvent, currentRow: ModelRow) {
        const changedRow = event.change.data as ModelRow;
        if (currentRow != null && !currentRow.isNull("lastUpdateNanos") &&
            currentRow.get("lastUpdateNanos") > changedRow.get("lastUpdateNanos")) {
            this.getLog().debug("Data change dropped due to stale data", event);
            event.preventDefault();
        }
        else {
            this.getLog().debug("Will handle data change:", event);
        }
    }

    adjustEventType(event: DataSourceRemoteDataChangeEvent, currentRow: ModelRow) {
        const changedRow = event.change.data as ModelRow;
        if (event.change.type !== ModelDataChangeType.DELETE && !this.filterObject.dataPasses(changedRow)) {
            event.change.type = ModelDataChangeType.DELETE;
        }

        if (event.change.type === ModelDataChangeType.ADD && currentRow != null) {
            event.change.type = ModelDataChangeType.UPDATE;
        }
    }

    handleSubscriberError(event: DataSourceSubscriberErrorEvent) {
        this.getLog().error(event.error);
        if (!this.showingErrorToast) {
            this.showingErrorToast = true;
            Toast.showToast("There was a problem keeping this page up-to-date real time.  Try refreshing the page.", null, {
                persist: true,
                onDismiss: () => this.showingErrorToast = false
            });
        }
    }

    startMinuteTimer(callback: () => void, trackedComponent: Component = this.getThisAsLayout()) {
        this.timer = new CallBackScheduler(trackedComponent, callback, DatePart.MINUTE, true);
    }

    styleLinkLabel(label: Label) {
        label.addMouseEnterListener(event => {
            label.underlineWhenMouseOver(event.target, true);
            label.setClassIncluded(LabelStyles.link, true);
        });
        label.addMouseLeaveListener(event => {
            label.underlineWhenMouseOver(event.target, false);
            label.setClassIncluded(LabelStyles.link, false);
        });
    }

    orderNumberOnClick(event: ClickEvent) {
        PlanningUtil.navigateToOrder(event);
    }

    labelEmptyOrderOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Label);
        const movementId = tableRow?.data?.get("movement_id");
        if (movementId)
            PlanningUtil.navigateToMovement(movementId, tableRow?.data?.get("order_id", null));
    }

    get findNearProps(): FindNearProps {
        return this._findNearProps;
    }

    set findNearProps(props: FindNearProps) {
        this._findNearProps = props;
    }
}
